import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import '../../styles/ClassDetailsPage.css';

const ClassDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showAddTeacherModal, setShowAddTeacherModal] = useState(false);
  const [personToRemove, setPersonToRemove] = useState(null);
  const [removeType, setRemoveType] = useState(null); // 'student' or 'teacher'
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [activeTab, setActiveTab] = useState('students'); // Changed default to students
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    schedule: '',
    location: '',
    center: '',
    active: true
  });

  useEffect(() => {
    const fetchClassDetails = async () => {
      try {
        setLoading(true);

        // Fetch class/halaqa details
        const classResponse = await api.get(`/halaqas/${id}`);
        const classData = classResponse.data.success
          ? classResponse.data.data
          : classResponse.data;

        // Extract frequency from description if available
        const { description: cleanDescription, frequency } = extractFrequencyFromDescription(classData.description);

        // Store updated data with extracted fields
        const enhancedClassData = {
          ...classData,
          description: cleanDescription,
          frequency: frequency || classData.frequency || ''
        };

        setClassData(enhancedClassData);

        // Also populate the edit form data with class details
        // Map time field to schedule for UI consistency
        setEditFormData({
          name: classData.name || '',
          description: cleanDescription || '',
          schedule: classData.time || classData.schedule || '',  // Use time field from backend if available
          frequency: frequency || classData.frequency || '',
          center: classData.center && typeof classData.center === 'object' && classData.center._id
            ? classData.center._id
            : typeof classData.center === 'string' && classData.center.trim() !== ''
              ? classData.center
              : '',
          active: classData.active !== undefined ? classData.active : true
        });

        // Fetch students
        const studentsResponse = await api.get(`/halaqas/${id}/students`);
        const studentsData = studentsResponse.data.success
          ? studentsResponse.data.data
          : studentsResponse.data;

        setStudents(Array.isArray(studentsData) ? studentsData : []);

        // Fetch teachers - following mobile app approach
        const teacherObjects = classData.teachers || [];

        // Extract teacher IDs, handling both object and string cases
        const teacherIds = teacherObjects.map(teacher => {
          if (typeof teacher === 'object' && teacher !== null && teacher._id) {
            return String(teacher._id);
          }
          return typeof teacher === 'string' ? teacher : String(teacher);
        }).filter(Boolean);

        // If teacher objects include complete data, use those directly
        if (teacherObjects.length > 0 && teacherObjects[0].name) {
          setTeachers(teacherObjects);
        } else if (teacherIds.length > 0) {
          // Otherwise fetch teacher details from the API
          const teachersResponse = await api.get('/users', {
            params: {
              ids: teacherIds.join(','),
              role: 'teacher'
            }
          });

          const teachersData = teachersResponse.data.success
            ? teachersResponse.data.data
            : teachersResponse.data;

          const assignedTeachers = Array.isArray(teachersData) ? teachersData : [];
          setTeachers(assignedTeachers);
        } else {
          // No teachers assigned
          setTeachers([]);
        }
      } catch (err) {
        console.error('Error fetching class details:', err);
        setError('Failed to load class details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchClassDetails();
  }, [id]);

  // Handle enabling edit mode
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  // Handle canceling edit mode
  const handleCancelEdit = () => {
    // Reset the form data to the current class data
    if (classData) {
      setEditFormData({
        name: classData.name || '',
        description: classData.description || '',
        schedule: classData.time || classData.schedule || '',  // Map time field to schedule for UI
        frequency: classData.frequency || '',
        center: classData.center && typeof classData.center === 'object' && classData.center._id
          ? classData.center._id
          : typeof classData.center === 'string' && classData.center.trim() !== ''
            ? classData.center
            : '',
        active: classData.active !== undefined ? classData.active : true
      });
    }
    // Exit edit mode
    setIsEditMode(false);
  };

  // Extract frequency from description if present
  const extractFrequencyFromDescription = (description) => {
    if (!description) return { description: '', frequency: '' };

    const frequencyMatch = description.match(/Frequency:\s*(.+?)(?:\n|$)/);
    if (frequencyMatch) {
      const frequency = frequencyMatch[1].trim();
      const cleanedDescription = description
        .replace(/Frequency:\s*.+?(?:\n|$)/, '')
        .trim();
      return { description: cleanedDescription, frequency };
    }

    return { description, frequency: '' };
  };

  // Handle form data changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle saving changes
  const handleSaveChanges = async () => {
    try {
      setLoading(true);

      // First, validate form
      if (!editFormData.name) {
        alert('Class name is required');
        setLoading(false);
        return;
      }

      // Get current user data to ensure we're maintaining center security
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;

      // Prepare data format for API - map our form fields to backend model fields
      const updatedData = {
        name: editFormData.name,
        description: editFormData.description,
        time: editFormData.schedule, // Map schedule to time for backend compatibility
        // Store frequency in the description field if it's provided
        ...(editFormData.frequency ? {
          description: editFormData.description
            ? `${editFormData.description}\n\nFrequency: ${editFormData.frequency}`
            : `Frequency: ${editFormData.frequency}`
        } : {}),
        active: editFormData.active
      };

      // Center security: Preserve the existing center/centerId
      // 1. If class already has a center, keep it
      // 2. If edit form has a valid center, use it
      // 3. For new classes, use the current user's center

      if (classData.center) {
        // For existing classes, we should preserve the original center
        if (typeof classData.center === 'object' && classData.center._id) {
          updatedData.centerId = classData.center._id;
          updatedData.center = classData.center._id;
          console.log('Using original center from class data (object):', classData.center._id);
        } else if (typeof classData.center === 'string' && classData.center.trim() !== '') {
          updatedData.centerId = classData.center;
          updatedData.center = classData.center;
          console.log('Using original center from class data (string):', classData.center);
        }
      } else if (editFormData.center && editFormData.center.trim() !== '') {
        // Use the center from the form data if available
        updatedData.centerId = editFormData.center;
        updatedData.center = editFormData.center;
        console.log('Using center from form data:', editFormData.center);
      } else if (user && user.center) {
        // Fallback to user's center if nothing else is available
        updatedData.centerId = user.center;
        updatedData.center = user.center;
        console.log('Using center from user data:', user.center);
      }

      console.log('Sending update with data:', updatedData);

      // Make the API call to update the class
      const response = await api.put(`/halaqas/${id}`, updatedData);

      // If successful, update the local state with new data
      if (response.data) {
        const updatedClass = response.data.success ? response.data.data : response.data;

        // Extract the frequency from description again for display
        const { description: cleanDescription, frequency } = extractFrequencyFromDescription(updatedClass.description);

        // Create enhanced data with our UI-specific fields
        const enhancedUpdatedClass = {
          ...updatedClass,
          description: cleanDescription,
          frequency: frequency,
          schedule: updatedClass.time // Map time back to schedule for UI
        };

        // Update the class data in state
        setClassData(prev => ({
          ...prev,
          ...enhancedUpdatedClass
        }));

        // Exit edit mode
        setIsEditMode(false);

        console.log('Class updated successfully:', enhancedUpdatedClass);
      }
    } catch (error) {
      console.error('Error updating class:', error);
      alert('Failed to update class. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClass = async () => {
    try {
      setLoading(true);
      
      // Send delete request to the API
      await api.delete(`/halaqas/${id}`);
      
      // Navigate back to classes list after successful deletion
      navigate('/classes');
    } catch (err) {
      console.error('Error deleting class:', err);
      alert('Failed to delete class. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      // Prepare update data - include center if available to maintain center security
      const updateData = {
        active: !classData.active
      };

      // Ensure center information is preserved when toggling status
      if (classData.center) {
        if (typeof classData.center === 'object' && classData.center._id) {
          // If center is an object with _id
          updateData.centerId = classData.center._id;
        } else if (typeof classData.center === 'string' && classData.center.trim() !== '') {
          // If center is a non-empty string
          updateData.centerId = classData.center;
        }
      }

      // Make API call with center-aware update data
      await api.put(`/halaqas/${id}`, updateData);

      // Update local state
      setClassData(prev => ({ ...prev, active: !prev.active }));

      // Close the confirmation modal
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Error updating class status:', err);
      alert('Failed to update class status');
    }
  };

  // Handle remove button click for student or teacher
  const handleRemoveClick = (person, type) => {
    setPersonToRemove(person);
    setRemoveType(type);
    setShowRemoveModal(true);
  };

  // Handle confirm removal of student
  const handleRemoveStudent = async (studentId) => {
    try {
      setLoading(true);

      // Call API to remove student from class
      await api.delete(`/students/${studentId}/halaqas/${id}`);

      // Update local state to remove student
      setStudents(prevStudents =>
        prevStudents.filter(student => student._id !== studentId)
      );

      // Close modal
      setShowRemoveModal(false);
      setPersonToRemove(null);
    } catch (err) {
      console.error('Error removing student:', err);
      alert('Failed to remove student. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle confirm removal of teacher
  const handleRemoveTeacher = async (teacherId) => {
    try {
      setLoading(true);

      // Call API to remove teacher from class
      await api.delete(`/halaqas/${id}/teachers/${teacherId}`);

      // Update local state to remove teacher
      setTeachers(prevTeachers =>
        prevTeachers.filter(teacher => teacher._id !== teacherId)
      );

      // Close modal
      setShowRemoveModal(false);
      setPersonToRemove(null);
    } catch (err) {
      console.error('Error removing teacher:', err);
      alert('Failed to remove teacher. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle confirm removal
  const handleRemoveConfirm = async () => {
    if (!personToRemove) return;

    if (removeType === 'student') {
      await handleRemoveStudent(personToRemove._id);
    } else if (removeType === 'teacher') {
      await handleRemoveTeacher(personToRemove._id);
    }
  };

  // Handle cancel removal
  const handleRemoveCancel = () => {
    setShowRemoveModal(false);
    setPersonToRemove(null);
    setRemoveType(null);
  };

  // Handle opening the Add Teacher modal
  const handleAddTeacherClick = async () => {
    try {
      setLoading(true);

      // Fetch all teachers who are not already assigned to this class
      const response = await api.get('/users', {
        params: {
          role: 'teacher'
        }
      });

      const allTeachers = response.data.success
        ? response.data.data
        : response.data;

      // Filter out teachers already assigned to this class
      const teacherIds = teachers.map(t => t._id);
      const unassignedTeachers = Array.isArray(allTeachers)
        ? allTeachers.filter(t => !teacherIds.includes(t._id))
        : [];

      setAvailableTeachers(unassignedTeachers);
      setSelectedTeacherId(unassignedTeachers.length > 0 ? unassignedTeachers[0]._id : '');
      setShowAddTeacherModal(true);
    } catch (err) {
      console.error('Error fetching available teachers:', err);
      alert('Failed to load available teachers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle teacher selection change in the dropdown
  const handleTeacherSelectChange = (e) => {
    setSelectedTeacherId(e.target.value);
  };

  // Handle adding the selected teacher to the class
  const handleAddTeacherConfirm = async () => {
    if (!selectedTeacherId) return;

    try {
      setLoading(true);

      // Call API to add teacher to class
      await api.post(`/halaqas/${id}/teachers/${selectedTeacherId}`);

      // Find the teacher object from available teachers
      const addedTeacher = availableTeachers.find(t => t._id === selectedTeacherId);

      // Update the teachers list
      if (addedTeacher) {
        setTeachers(prev => [...prev, addedTeacher]);
      }

      // Close the modal
      setShowAddTeacherModal(false);
      setSelectedTeacherId('');
    } catch (err) {
      console.error('Error adding teacher:', err);
      alert('Failed to add teacher. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel adding teacher
  const handleAddTeacherCancel = () => {
    setShowAddTeacherModal(false);
    setSelectedTeacherId('');
  };

  return (
    <div className="class-details-container">
      <div className="header">
        <h1>Class Details</h1>
        <div className="header-actions">
          <Link to="/classes" className="back-btn">
            Back to Classes
          </Link>
          {!isEditMode ? (
            <button onClick={handleEditClick} className="edit-class-btn">
              Edit Class
            </button>
          ) : (
            <div className="edit-mode-actions">
              <button onClick={handleCancelEdit} className="cancel-edit-btn">
                Cancel
              </button>
              <button onClick={handleSaveChanges} className="save-btn">
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading class details...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : classData ? (
        <div className="class-details-content">
          <div className="class-info-card">
            {!isEditMode ? (
              <>
                <div className="class-header">
                  <div className="class-title">
                    <h2>{classData.name}</h2>
                    <div className="class-meta">
                      <span className={`status-badge ${classData.active ? 'status-active' : 'status-inactive'}`}>
                        {classData.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="status-toggle">
                    <div className="status-actions">
                      <button
                        className={`status-toggle-btn ${classData.active ? 'deactivate' : 'activate'}`}
                        onClick={() => setShowConfirmModal(true)}
                      >
                        {classData.active ? 'Deactivate Class' : 'Activate Class'}
                      </button>
                      <button
                        className="delete-class-btn"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        Delete Class
                      </button>
                    </div>
                  </div>
                </div>

                <div className="info-section">
                  <div className="info-item">
                    <span className="label">Description:</span>
                    <span className="value">{classData.description || 'No description provided'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Schedule:</span>
                    <span className="value">{classData.time || classData.schedule || 'Not scheduled'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Frequency:</span>
                    <span className="value">{classData.frequency || 'Not specified'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Created:</span>
                    <span className="value">{new Date(classData.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="edit-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Class Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={editFormData.name}
                      onChange={handleInputChange}
                      placeholder="Enter class name"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={editFormData.description}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Provide a brief description of the class"
                    ></textarea>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="schedule">Schedule</label>
                    <input
                      type="text"
                      id="schedule"
                      name="schedule"
                      value={editFormData.schedule}
                      onChange={handleInputChange}
                      placeholder="E.g., Mon, Tue, Wed"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="frequency">Frequency</label>
                    <input
                      type="text"
                      id="frequency"
                      name="frequency"
                      value={editFormData.frequency || ''}
                      onChange={handleInputChange}
                      placeholder="E.g., Weekly, Daily, Monthly"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="active">Status</label>
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="active"
                        name="active"
                        checked={editFormData.active}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="active" className="checkbox-label">
                        Active
                      </label>
                    </div>
                    <small className="helper-text">
                      Inactive classes won't be visible to students and teachers
                    </small>
                  </div>

                  {/* Hidden fields for data we need to maintain but not display in UI */}
                  <input
                    type="hidden"
                    name="center"
                    value={editFormData.center || ''}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="tabs-container">
            <div className="tabs">
              <button
                className={`tab ${activeTab === 'students' ? 'active' : ''}`}
                onClick={() => setActiveTab('students')}
              >
                Students ({students.length})
              </button>
              <button
                className={`tab ${activeTab === 'teachers' ? 'active' : ''}`}
                onClick={() => setActiveTab('teachers')}
              >
                Teachers ({teachers.length})
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'students' && (
                <div className="students-tab">
                  {students.length > 0 ? (
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map(student => (
                            <tr key={student._id}>
                              <td>{student.name}</td>
                              <td>{student.email}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="info-message">
                        <i>Use the Manage Users page to add/update students in this class.</i>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No students enrolled in this class yet.</p>
                      <p><i>Use the Manage Users page to add students to this class.</i></p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'teachers' && (
                <div className="teachers-tab">
                  <div className="tab-header">
                    <h3>Teachers</h3>
                    <button
                      className="add-teacher-btn"
                      onClick={handleAddTeacherClick}
                    >
                      Add Teacher
                    </button>
                  </div>

                  {teachers.length > 0 ? (
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teachers.map(teacher => (
                            <tr key={teacher._id}>
                              <td>{teacher.name}</td>
                              <td>{teacher.email}</td>
                              <td>
                                <button
                                  className="remove-btn"
                                  onClick={() => handleRemoveClick(teacher, 'teacher')}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="info-message teacher-message">
                        <i>Only teachers assigned to this class are shown here.</i>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No teachers assigned to this class yet.</p>
                      <p>Use the Add Teacher button to assign teachers to this class.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="not-found">Class not found</div>
      )}

      {/* Delete Class Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Delete Class</h3>
            <p>
              Are you sure you want to delete this class? This action cannot be undone.
              All assignments and attendance records associated with this class will also be deleted.
            </p>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn danger"
                onClick={handleDeleteClass}
              >
                Delete Class
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>
              {classData.active
                ? 'Are you sure you want to deactivate this class?'
                : 'Are you sure you want to activate this class?'}
            </h3>
            <p>
              {classData.active
                ? 'Students and teachers will no longer be able to access this class.'
                : 'Students and teachers will be able to access this class again.'}
            </p>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                className={`confirm-btn ${classData.active ? 'danger' : 'success'}`}
                onClick={handleToggleActive}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Person Confirmation Modal */}
      {showRemoveModal && personToRemove && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Remove {removeType === 'student' ? 'Student' : 'Teacher'}</h3>
            <p>
              Are you sure you want to remove {personToRemove.name} from this class?
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={handleRemoveCancel}
              >
                Cancel
              </button>
              <button
                className="confirm-btn danger"
                onClick={handleRemoveConfirm}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Teacher Modal */}
      {showAddTeacherModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Add Teacher to Class</h3>

            {availableTeachers.length > 0 ? (
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="teacherSelect">Select a teacher:</label>
                  <select
                    id="teacherSelect"
                    value={selectedTeacherId}
                    onChange={handleTeacherSelectChange}
                    className="teacher-select"
                  >
                    {availableTeachers.map(teacher => (
                      <option key={teacher._id} value={teacher._id}>
                        {teacher.name} ({teacher.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="modal-actions">
                  <button
                    className="cancel-btn"
                    onClick={handleAddTeacherCancel}
                  >
                    Cancel
                  </button>
                  <button
                    className="confirm-btn success"
                    onClick={handleAddTeacherConfirm}
                    disabled={!selectedTeacherId}
                  >
                    Add Teacher
                  </button>
                </div>
              </div>
            ) : (
              <div className="modal-content">
                <p>No available teachers found. All teachers are already assigned to this class.</p>
                <div className="modal-actions">
                  <button
                    className="cancel-btn"
                    onClick={handleAddTeacherCancel}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassDetailsPage;